import SelectableTile from '@carvana/ds-selectable-tile';
import omit from 'lodash.omit';
import { useContext, useState } from 'react';
import {
  CardImage,
  HeadlineRow,
  ImageContainer,
  ImageUnavailable,
  PriceContainer,
  PriceLabel,
  PriceValue,
  StockCount,
  Title
} from '@/new-cars/common/ConfiguratorCard.styles';
import tileStyles from '@/new-cars/common/SelectableTile.module.css';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type { YearResult } from '~/contracts/new-cars';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { isFallbackImage } from '~/utilities/isFallbackImage';
import optimizeCardImage from '~/utilities/new-cars/optimizeCardImage';
import { Content, Subtitle } from './ModelVersionCard.styles';

type OwnProps = {
  disabled?: boolean;
  modelVersion: YearResult;
  selected: boolean;
};

export default function ModelVersionCard({
  disabled = false,
  modelVersion,
  selected
}: OwnProps) {
  const { setSelectedModelVersion } = useContext(ParentModelContext);
  const [isImageBroken, setIsImageBroken] = useState(false);

  const { count, description, imageUrl, lowestPrice, title } = modelVersion;
  const isFallback = isFallbackImage(imageUrl);
  const optimizedImageUrl = optimizeCardImage(imageUrl, isFallback);

  return (
    <SelectableTile
      disabled={disabled}
      selected={selected}
      onClick={() => {
        setSelectedModelVersion(
          omit({ ...modelVersion, ...modelVersion.next }, 'next')
        );
      }}
      data-analytics-attributes={JSON.stringify({
        level: 'Model Version',
        section: 'Model Version',
        text: title,
        tileAction: selected ? 'selected' : 'deselected'
      })}
      data-analytics-id="New Cars"
      data-testid="model-version-card"
      value={title}
      variant="simple-select"
      className={tileStyles.tile}
    >
      <ImageContainer disabled={disabled} isFallback={isFallback}>
        {optimizedImageUrl && !isImageBroken ? (
          <CardImage
            alt={title}
            data-testid="model-version-card-image"
            onError={() => setIsImageBroken(true)}
            src={optimizedImageUrl}
          />
        ) : (
          <ImageUnavailable>Image unavailable</ImageUnavailable>
        )}
      </ImageContainer>
      <Content>
        <HeadlineRow>
          <Title disabled={disabled}>{title}</Title>
          <PriceContainer data-testid="model-version-card-price">
            <PriceLabel>from </PriceLabel>
            <PriceValue disabled={disabled}>
              ${formatAndDropDecimal(lowestPrice)}
            </PriceValue>
          </PriceContainer>
        </HeadlineRow>
        <Subtitle disabled={disabled}>{description}</Subtitle>
        <StockCount data-testid="model-version-card-stock-count">
          {count} in stock
        </StockCount>
      </Content>
    </SelectableTile>
  );
}
